import React, { useRef, useState } from 'react'
import { CalendarOutlined } from '@ant-design/icons'
import QuMap from '@quiqupltd/QuReactSDK/lib/components/qu-map'
import VectorBasemap from '../../map/vector-basemap'
import {
  CardTitle,
  HeaderInfo,
  MapScrollOverlay,
  MapWrapper,
  StatusInfo,
} from '../../../v2/styles'
import HeaderContent from './header-content'
import EcoDeliveryBadge from './eco-delivery-badge'
import RobotWidget from './robot-widget'
import OrderStatesList from '../order-state-list/order-states-list'
import { useOnClickOutside } from '../utils'
import { useEnvVars } from '../../../hooks/useEnvVars'
import { useTracking } from '../../../hooks/useTracking'
import { externalStatus } from '../../../lib/status'
import { withRobotStep } from '../../../lib/robot-delivery'
import { isSaudiDomestic } from '../../../utils/country'

type Props = {
  countryCode: string
}

/** A 1x1 fully transparent GIF, as a data URI — no network request, draws nothing. */
const TRANSPARENT_TILE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export default function Header(props: Props) {
  const { countryCode } = props
  const [mapActive, setMapActive] = useState(false)
  const [withBounds, setWithBounds] = useState(true)
  const mapRef = useRef(null)

  const {
    orderDetails,
    mapCenter,
    liveOrder,
    uniqueStateChanges,
    hasMap,
    hasWidget,
  } = useTracking()
  const { env } = useEnvVars()

  useOnClickOutside(mapRef, () => setMapActive(false))

  const disableWithBound = () => {
    setWithBounds(false)
  }

  if (!orderDetails) {
    return null
  }

  const hideEta = isSaudiDomestic(
    orderDetails.origin?.address?.country,
    orderDetails.destination?.address?.country
  )

  // Nabd's widget takes the map slot for the robot leg (sc-29236); the
  // courier map is meaningless once the parcel is inside the building.
  const mapSlotFilled = hasMap || hasWidget

  return (
    <HeaderInfo mapExist={mapSlotFilled} hideEta={hideEta}>
      {orderDetails?.ecoDelivery ? <EcoDeliveryBadge /> : null}
      {!hideEta && (
        <>
          <div className="info-icon">
            <CalendarOutlined />
          </div>
          <HeaderContent
            state={externalStatus[uniqueStateChanges[0]?.toState ?? '']}
            date={
              externalStatus[uniqueStateChanges[0]?.toState ?? ''] ===
                'Delivered' || orderDetails?.serviceKind === 'partner_return'
                ? uniqueStateChanges[0]?.occurredAt
                : orderDetails?.deliveryTime?.deliveryBefore
            }
            countryCode={countryCode}
          />
        </>
      )}
      {orderDetails?.id ? (
        <StatusInfo>
          <CardTitle className="status-title">
            Order status: #{orderDetails?.id}
          </CardTitle>
          <OrderStatesList
            uniqueStateChanges={withRobotStep(
              uniqueStateChanges,
              orderDetails.robotDelivery
            )}
          />
        </StatusInfo>
      ) : null}
      {hasWidget && orderDetails.robotDelivery?.widgetUrl ? (
        <RobotWidget widgetUrl={orderDetails.robotDelivery.widgetUrl} />
      ) : hasMap ? (
        <MapWrapper ref={mapRef}>
          <QuMap
            /**
             * QuMap renders its own raster <TileLayer> from `tileUrl` and offers no
             * way to omit it, so we point it at a transparent 1x1 pixel — it draws
             * nothing, makes no network requests, and the <VectorBasemap> child
             * below becomes the real basemap. Removing the layer at runtime instead
             * would be layer surgery on someone else's component.
             *
             * The SDK's default `tileUrl` also has the MapTiler key hardcoded in
             * its published source, so overriding it here is what actually gets
             * this surface off MapTiler.
             */
            tileUrl={TRANSPARENT_TILE}
            geoJson={
              liveOrder?.features?.length > 0 && {
                features: liveOrder?.features,
              }
            }
            center={mapCenter}
            withBounds={withBounds}
            maxZoom={18}
            onZoom={disableWithBound}
            onDragend={disableWithBound}
            mobile={true}
          >
            <VectorBasemap styleUrl={env.TILES_STYLE_URL ?? ''} />
          </QuMap>
          <MapScrollOverlay
            isMapActive={mapActive}
            onClick={(e) => {
              setMapActive(true)
              e.stopPropagation()
            }}
          />
        </MapWrapper>
      ) : null}
    </HeaderInfo>
  )
}
